import React from "react";
import { strings } from "../../localization/strings";
import { strings as coreStrings } from '@piceasoft/core'
import { FontWeights, getTheme, IconButton, mergeStyleSets, MessageBar, MessageBarType, Modal, Spinner, Stack, StackItem } from "@fluentui/react";
import { ResetPasswordForm } from "../../components/shared/forms/ResetPasswordForm";
import { portalApi } from "../../core/api/api";

type TProps = {
    token: string
    userId?: string
    lang?: string
}

const PasswordReset: React.FC<TProps> = (props) => {
    const [isValidToken, setIsValidToken] = React.useState<boolean>(false)
    const [targetAccount, setTargetAccount] = React.useState<string>('');
    const [roles, setRoles] = React.useState<string[]>([]);
    const [error, setError] = React.useState<string>("");
    const [redirectUrl, setRedirectUrl] = React.useState<string>('');
    const [spinnerLabel, setSpinnerLabel] = React.useState<string>('');

    const resetPassword = async (newPassword: string) => {
        const resp = await portalApi.organization.accounts.resetPasswordWithToken( props.userId ?? '', props.token, newPassword);
        if( resp.successed) {
            setError( '');
            setIsValidToken( false);
            setSpinnerLabel( strings.CONSTRUCTOR.RESET_PASSWORD.SUCCEEDED);

            setTimeout( () => {
                window.location.href = resp.data?.redirectUrl ?? '/';
            }, 3000)
        } else {
            setError( strings.CONSTRUCTOR.RESET_PASSWORD.RESET_ERROR);
        }
    }

    const dismiss = () => {
        window.location.href = redirectUrl && '' != redirectUrl ? redirectUrl :'/';
    }

    React.useEffect( () => {
        if( props.lang)  {
            const lang = (props.lang.toUpperCase() === "PT-BR") ? "pt-br": (props.lang ?? "en-US").slice(0,2);

            strings.setLanguage( lang);
            coreStrings.setLanguage( lang);
        }
    }, [props.lang])

    React.useEffect( () => {
        if( !props.userId || '' === props.userId) {
            setError( strings.CONSTRUCTOR.RESET_PASSWORD.LINK_ERROR);
            return;
        }
        portalApi.organization.accounts.validatePasswordToken( props.userId ?? '', props.token).then( r => {
            if( r.successed) {
                setIsValidToken( true);
                setTargetAccount( r.data?.username ?? '');
                setRedirectUrl( r.data?.redirectUrl ?? '');
                setRoles( r.data?.roles ?? []);
            } else {
                setTargetAccount('');
                setRoles( []);
                setError( strings.CONSTRUCTOR.RESET_PASSWORD.LINK_ERROR);
            }
        })

    }, [props.token, props.userId])

    if( "" !== error) {
        return (
        <Stack verticalFill verticalAlign="center" horizontalAlign="center" grow>
            <StackItem style={{maxWidth: '600px'}}>
                <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
            </StackItem>
        </Stack>
        )
    }

    if( isValidToken) {
        return (
            <Modal isOpen={true} onDismiss={dismiss} containerClassName={contentStyles.container}>
                <div className={contentStyles.header}>
                    <span style={{ paddingRight: 32 }}>{strings.ORGANIZATION.STRUCTURE.ITEM.PERSON.RESET_PASSWORD_TITLE}</span>
                    <IconButton
                        styles={iconButtonStyles}
                        iconProps={{ iconName: 'Cancel' }}
                        ariaLabel="Close window"
                        onClick={dismiss}
                    />
                </div>
                <div className={contentStyles.body}>
                    <Stack verticalFill tokens={{ childrenGap: 12 }}>
                        <ResetPasswordForm fetched={false} 
                                            targetAccount={targetAccount}
                                            onCancel={dismiss} 
                                            onSubmit={resetPassword} 
                                            roles={roles}
                                            noDefault />
                    </Stack>
                </div>
            </Modal>
        )
    }

    return (
        <Stack verticalFill verticalAlign="center" horizontalAlign="center" grow>
            <Spinner label={ '' !== spinnerLabel ? spinnerLabel : strings.SPINNERS.PLEASE_WAIT} />
        </Stack>
    )
}
export default PasswordReset

const theme = getTheme();

const iconButtonStyles = {
    root: {
        color: theme.palette.neutralPrimary,
        marginLeft: 'auto',
        marginTop: '4px',
        marginRight: '2px',
    },
    rootHovered: {
        color: theme.palette.neutralDark,
    },
};

const contentStyles = mergeStyleSets({
    container: {
        display: 'flex',
        flexFlow: 'column nowrap',
        alignItems: 'stretch',
        width: 480
    },
    header: [
        theme.fonts.xLarge,
        {
            flex: '1 1 auto',
            borderTop: `4px solid ${theme.palette.themePrimary}`,
            color: theme.palette.neutralPrimary,
            display: 'flex',
            alignItems: 'center',
            fontWeight: FontWeights.semibold,
            padding: '12px 12px 14px 24px',
        },
    ],
    body: {
        position: "relative",
        flex: '4 4 auto',
        padding: '0px 24px 24px 24px',
        overflowY: 'hidden',
        selectors: {
            p: { margin: '14px 0' },
            'p:first-child': { marginTop: 0 },
            'p:last-child': { marginBottom: 0 },
        },
    },
});